import { Event } from "Global/Base/Events";
import { InvalidArgumentException } from "Global/Base/Exceptions";
import { IDisposable } from "Global/Base/IDisposable";
import { IDataEventsManager } from "OW.Remoting/Remoting/IDataEventsManager";
import { EventsController } from "../../../Global/Dom/EventsController";
import { Control } from "./Controls/Control";
import { FormManager } from "./FormManager/IFormManager";
import { WindowState } from "./UIManagerInterfaces";
import { IFrame } from "./XGControls/IFrame";

/**
* Hlídá změny dat konkrétní třídy a na základě změny dat a aktivnosti komponenty vyvolává událost, která určuje, že by měl proběhnout refresh
*/
export class AutoRefresher implements IDisposable {
    /**
     * Událost vyvolaná v okamžiku, kdy by měl proběhnout refresh komponenty
     */
    public get onRefresh(): Event<Control, void> {
        return this._onRefresh;
    }

    private _canPerformRefresh: boolean = true;
    private readonly _control: Control;
    private readonly _classNames: Array<string>;
    private readonly _dataEventsManager: IDataEventsManager;
    private readonly _onRefresh: Event<Control, void>;

    private readonly _refreshTimeoutMilliseconds: number;
    private _refreshTimeout: NodeJS.Timeout;
    private _refreshTimeoutRunning: boolean;

    private _classDataChangedListener: () => void;

    private _pendingDataChanged: boolean = false;
    private _pageVisible: boolean;
    private _controlActive: boolean;
    private _ownerFormInteractive: boolean = true;

    private _eventsController = new EventsController();

    private get _shouldPerformRefresh(): boolean {
        return this._pageVisible && this._controlActive && this._ownerFormInteractive && this._canPerformRefresh;
    }

    /**
     * Konstruktor třídy pro obsluhu auto refreshů.
     * @param control Komponenta, která se refreshuje.
     * @param classNames Názvy tříd, u kterých se při změně má vyvolat refresh.
     * @param dataEventsManager Manažer data eventů.
     * @param refreshTimeout Timeout od posledního refreshe (v sekundách), po dobu kterého se má pozdržet opakovaný refresh. 0 = bez timeoutu.
     */
    constructor(control: Control, classNames: string[], dataEventsManager: IDataEventsManager, refreshTimeout: number = 0) {
        if (!control)
            throw new InvalidArgumentException("control");
        if (!classNames)
            throw new InvalidArgumentException("className");

        this._control = control;
        this._classNames = classNames;
        this._dataEventsManager = dataEventsManager;
        this._onRefresh = new Event<Control, void>();
        this._controlActive = this._control.canValueLoad;
        this._refreshTimeoutMilliseconds = refreshTimeout * 1000;

        if (this._refreshTimeoutMilliseconds > 0) {
            this._setRefreshTimeout();
        }

        this._initializeListeners();
    }

    public pause(): void {
        this._canPerformRefresh = false;
    }

    public unpause(): void {
        if (this._canPerformRefresh)
            return;

        this._canPerformRefresh = true;
        this._checkChanges();
    }

    private _initializeDataEventListeners(): void {
        for (const className of this._classNames) {
            this._dataEventsManager.subscribeClassChanges(className, this._classDataChangedListener = () => this._onClassDataChangedEvent());
        }
    }

    private async _initializeListeners(): Promise<void> {
        this._pageVisible = await this._isPageVisible();

        this._eventsController.addListener(document, "visibilitychange", () => this._pageVisibilityChanged());
        this._eventsController.add(this._control.canValueLoadChanged, (_sender, active) => this._activeStateChanged(active));

        if (this._control.canValueLoad) {
            this._initializeDataEventListeners();
        }

        if (this._control instanceof IFrame && this._control.ownerForm) {
            this._eventsController.add(this._control.ownerForm.readOnlyChanged, (_form, readOnly) => this._ownerFormInteractiveChanged(!readOnly));
            this._eventsController.add(this._control.ownerForm.stateChanged, (_s, state) => {
                if (state.state === WindowState.visible || state.state === WindowState.realVisibility) {
                    this._pageVisibilityChanged();
                }
            });
        }
    }

    private _onClassDataChangedEvent(): void {
        if (!this._shouldPerformRefresh) {
            this._pendingDataChanged = true;
            return;
        }

        this._triggerRefresh();
    }

    private _triggerRefresh(): void {
        if (this._refreshTimeoutRunning) {
            this._pendingDataChanged = true;
            return;
        }

        this._pendingDataChanged = false;
        this._onRefresh.trigger(this._control, undefined);

        if (this._refreshTimeoutMilliseconds > 0) {
            this._setRefreshTimeout();
        }
    }

    private async _activeStateChanged(active: boolean): Promise<void> {
        this._controlActive = active;

        this._checkChanges();
    }

    private async _pageVisibilityChanged(): Promise<void> {
        this._pageVisible = await this._isPageVisible();

        this._checkChanges();
    }

    private async _ownerFormInteractiveChanged(interactive: boolean): Promise<void> {
        this._pageVisible = await this._isPageVisible();
        this._ownerFormInteractive = interactive;

        this._checkChanges();
    }

    private _checkChanges(): void {
        if (!this._shouldPerformRefresh)
            return;

        if (!this._classDataChangedListener) { // při prvním zaktivnění controlu se zaregistruje naslouchání událostem
            this._initializeDataEventListeners();
        }

        if (!this._pendingDataChanged)
            return;

        this._triggerRefresh();
    }

    /**
     * Zda je vidět stránka prohlížeče a zda je videt dany form
     */
    private async _isPageVisible(): Promise<boolean> {
        if (this._control instanceof IFrame) {
            if (FormManager.instance.openingFormPromise) await FormManager.instance.openingFormPromise;

            return !document.hidden && this._control.ownerForm?.isFormVisible;
        } else return !document.hidden;
    }

    /** Nastaví timeout, během kterého nedojde k auto refreshi. Pokud přišel požadavek na refresh během timeoutu, tak se spustí po jeho doběhnutí. */
    private _setRefreshTimeout(): void {
        this._refreshTimeoutRunning = true;
        this._refreshTimeout = setTimeout(() => {
            this._refreshTimeoutRunning = false;
            this._checkChanges();
        }, this._refreshTimeoutMilliseconds);
    }

    /** Resetuje timeout pro auto refresh. */
    public resetRefreshTimeout(): void {
        if (this._refreshTimeoutMilliseconds > 0) {
            this._pendingDataChanged = false;
            clearTimeout(this._refreshTimeout);
            this._setRefreshTimeout();
        }
    }

    public dispose(): void {
        this._canPerformRefresh = false;
        this.onRefresh.removeAll();
        this._eventsController.dispose();
        if (this._classDataChangedListener) {
            for (const className of this._classNames) {
                this._dataEventsManager.unsubscribeClassChanges(className, this._classDataChangedListener);
            }
            this._classDataChangedListener = null;
        }
    }
}
